import {
  FormControl,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  SxProps,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { BLUE_GRAY_200, GRAY_500, PRIMARY } from '../../constants/Colors';
import { t } from 'i18next';
import {
  LANGUAGES_ARRAY,
  MESSAGE_STATUS_ARRAY,
  PAYMENT_METHODS_ARRAY,
  PAYMENT_TERMS_ARRAY,
  PRIORITY_ARRAY,
  SETUP_FEE_ARRAY,
  SHIP_TO_ARRAY,
  SHIPPIING_CALCULATION_ARRAY,
  SUBSCRIPTION_PACK_ARRAY,
  YES_NO_ARRAY,
  YES_NO_ARRAY_FR,
} from '../../constants/Constants';
import React from 'react';
import { BakeasyAvatar } from '../common/BakeasyAvatar';
import i18n from '../../languages/languages';

interface BakeasySelectProps {
  label?: string;
  value?: any;
  disabled?: boolean;
  statusOptions?: string[];
  fullWidth?: boolean;
  size?: any;
  required?: any;
  placeholder?: string;
  label1?: string;
  renderValue?:
    | ((selected: HTMLSelectElement) => JSX.Element | undefined)
    | undefined;
  onChange: (event: SelectChangeEvent<HTMLSelectElement>) => void;
  options?: {}[];
}

export const selectSxProps: SxProps = {
  borderRadius: 2,
  '&.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
    borderWidth: 1,
    borderColor: BLUE_GRAY_200,
  },
  '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderWidth: 1.5,
    borderColor: PRIMARY,
  },
};

export function BakeasySelect(props: BakeasySelectProps) {
  return (
    <FormControl
      sx={{ minWidth: 120 }}
      size={props?.size ? props?.size : 'medium'}
      fullWidth={props.fullWidth}
    >
      <InputLabel>{props.label} </InputLabel>
      <Select
        style={{ textAlign: 'start' }}
        label={props.label}
        value={props.value}
        onChange={props.onChange}
        sx={selectSxProps}
      >
        <MenuItem value={''}>
          <div
            style={{
              color: GRAY_500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {props?.label ?? ''}
          </div>
        </MenuItem>
        {props?.statusOptions?.map((status) => (
          <MenuItem key={status} value={status}>
            {status}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export function BakeasySelects(props: BakeasySelectProps) {
  return (
    <FormControl
      sx={{ minWidth: 120 }}
      size={props?.size ?? 'small'}
      fullWidth={props.fullWidth ?? true}
    >
      <FormLabel
        sx={{ fontWeight: 'bold', fontSize: '0.95rem', marginBottom: 0.5 }}
      >
        {props?.label ? t(props?.label) : ''}
        {props?.required}
      </FormLabel>

      <Select
        disabled={props.disabled ?? false}
        style={{ textAlign: 'start' }}
        value={props?.value}
        onChange={props?.onChange}
        displayEmpty
        sx={selectSxProps}
      >
        <MenuItem value={''}>
          <div
            style={{
              color: GRAY_500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {props.placeholder
              ? props?.placeholder
              : t(props?.label || '')}{' '}
          </div>
        </MenuItem>
        {props?.options?.map((option: any, index: any) => (
          <MenuItem key={index} value={option?.id}>
            {t(option?.value)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export function BakeasySelectLabel(props: BakeasySelectProps) {
  return (
    <FormControl
      size={props?.size ? props?.size : 'medium'}
      fullWidth={props.fullWidth}
    >
      <FormLabel
        sx={{ fontWeight: 'bold', fontSize: '0.95rem', marginBottom: 0.5 }}
      >
        {props.label}
        {props?.required}
      </FormLabel>

      <Select
        disabled={props.disabled ?? false}
        style={{ textAlign: 'start' }}
        value={props.value}
        onChange={props.onChange}
        displayEmpty
        renderValue={(selected: any) => {
          if (selected?.length === 0) {
            return <div style={{ color: GRAY_500 }}>{props?.placeholder}</div>;
          }
          return selected;
        }}
        sx={selectSxProps}
      >
        <MenuItem value={''}>
          <div
            style={{
              color: GRAY_500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {props?.placeholder}
          </div>
        </MenuItem>
        {props?.statusOptions?.map((status) => (
          <MenuItem key={status} value={status}>
            {status}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export function Category(props: BakeasySelectProps) {
  return (
    <FormControl
      sx={{ minWidth: 120 }}
      size={props?.size ?? 'small'}
      fullWidth={props.fullWidth ?? true}
      disabled={props.disabled ?? false}
    >
      <FormLabel
        sx={{ fontWeight: 'bold', fontSize: '0.95rem', marginBottom: 0.5 }}
      >
        {props?.label ? t(props.label) : t('CATEGORY')}
        {props?.required}
      </FormLabel>

      <Select
        style={{ textAlign: 'start' }}
        value={props?.value}
        onChange={props.onChange}
        displayEmpty
        sx={selectSxProps}
      >
        <MenuItem value={''}>
          <div
            style={{
              color: GRAY_500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {props?.placeholder ?? t('SELECT_CATEGORY')}
          </div>
        </MenuItem>
        {props?.options?.map((option: any, index: any) => (
          <MenuItem key={index} value={option?._id}>
            {option?.categoryName}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export function SubCategory(props: BakeasySelectProps) {
  return (
    <FormControl
      sx={{ minWidth: 120 }}
      size={props?.size ?? 'small'}
      fullWidth={props.fullWidth ?? true}
      disabled={props.disabled ?? false}
    >
      <FormLabel
        sx={{ fontWeight: 'bold', fontSize: '0.95rem', marginBottom: 0.5 }}
      >
        {props?.label ? t(props.label) : t('SUBCATEGORY')}
        {props?.required}
      </FormLabel>

      <Select
        style={{ textAlign: 'start' }}
        value={props?.value}
        onChange={props.onChange}
        displayEmpty
        sx={selectSxProps}
      >
        <MenuItem value={''}>
          <div
            style={{
              color: GRAY_500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {props?.placeholder ?? t('SELECT_SUB_CATEGORY')}
          </div>
        </MenuItem>
        {props?.options?.map((option: any, index: any) => (
          <MenuItem key={index} value={option?._id}>
            {option?.subCategoryName}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export function BakeasySelectLabelLanguage(props: BakeasySelectProps) {
  return (
    <FormControl
      sx={{ minWidth: 120 }}
      size={props?.size ?? 'small'}
      fullWidth={props.fullWidth ?? true}
    >
      <FormLabel
        sx={{ fontWeight: 'bold', fontSize: '0.95rem', marginBottom: 0.5 }}
      >
        {props?.label ? t(props.label) : t('LANGUAGE')}
        {props?.required}
      </FormLabel>

      <Select
        style={{ textAlign: 'start' }}
        value={props?.value}
        onChange={props?.onChange}
        displayEmpty
        sx={selectSxProps}
      >
        <MenuItem value={''}>
          <div
            style={{
              color: GRAY_500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {props?.placeholder ?? t('SELECT_LANGUAGE')}
          </div>
        </MenuItem>
        {LANGUAGES_ARRAY?.map((option: any, index: any) => (
          <MenuItem key={index} value={option?.id}>
            {option?.value}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export function BakeasySelectLabelProfile(props: BakeasySelectProps) {
  let theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [showSelect, setShowSelect] = React.useState(false);
  let savedAuth = localStorage.getItem('auth');
  let parsedAuth = JSON.parse(savedAuth!);

  return (
    <FormControl
      key={props?.label}
      size={props?.size ?? 'small'}
      fullWidth={props.fullWidth ?? true}
    >
      <Select
        key={props?.label}
        open={showSelect}
        onOpen={() => setShowSelect(true)}
        onClose={() => setShowSelect(false)}
        style={{ textAlign: 'start' }}
        value={props?.value}
        onChange={props.onChange}
        displayEmpty
        renderValue={(selected: any) => {
          if (selected?.length === 0) {
            return (
              <Grid container columnGap={1}>
                {isMobile && (
                  <BakeasyAvatar
                    height={24}
                    width={24}
                    value={parsedAuth?.userDetails?.userInfo?.name}
                  />
                )}
                {!isMobile && (
                  <BakeasyAvatar
                    height={24}
                    width={24}
                    value={parsedAuth?.userDetails?.userInfo?.name}
                  />
                )}
                {!isMobile && t('PROFILE_SETTINGS')}
              </Grid>
            );
          }
          return selected;
        }}
        sx={selectSxProps}
      >
        {props?.options?.map((item: any, index: number) => (
          <MenuItem
            key={index}
            sx={{ marginX: 0, marginY: 1 }}
            value={item?.id}
            onClick={(event: any) => {
              item.onClick(event);
              setShowSelect(false);
            }}
          >
            {item.icon}
            {t(item?.lable)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

// export function BakeasySelectState(props: BakeasySelectProps) {
//   return (
//     <BakeasySelects
//       label={t('STATE')}
//       placeholder={t('SELECT') + t('STATE')}
//       size="small"
//       options={STATES_ARRAY}
//       fullWidth={true}
//       value={props?.value}
//       onChange={props?.onChange}
//     />
//   );
// }

// export function BakeasySelectCity(props: BakeasySelectProps) {
//   return (
//     <BakeasySelects
//       label={t('CITY')}
//       placeholder={t('SELECT') + t('CITY')}
//       size="small"
//       options={CITIES_ARRAY}
//       fullWidth={true}
//       value={props?.value}
//       onChange={props?.onChange}
//     />
//   );
// }

// export function BakeasySelectStore(props: BakeasySelectProps) {
//   return (
//     <BakeasySelects
//       label={t('STORE')}
//       placeholder={t('SELECT') + t('STORE')}
//       size="small"
//       options={STORES_ARRAY}
//       fullWidth={true}
//       value={props?.value}
//       onChange={props?.onChange}
//     />
//   );
// }

// export function BakeasySelectProduct(props: BakeasySelectProps) {
//   return (
//     <BakeasySelects
//       label={t('PRODUCT')}
//       placeholder={t('SELECT') + t('PRODUCT')}
//       size="small"
//       options={PRODUCTS_ARRAY}
//       fullWidth={true}
//       value={props?.value}
//       onChange={props?.onChange}
//       required={props?.required}
//     />
//   );
// }

export function Status(props: BakeasySelectProps) {
  return (
    <BakeasySelects
      label={t('STATUS')}
      placeholder={t('SELECT_STATUS')}
      size="small"
      options={props?.options}
      fullWidth={true}
      value={props?.value}
      required={props?.required ?? false}
      onChange={props?.onChange}
      disabled={props?.disabled}
    />
  );
}

// export function Orders(props: BakeasySelectProps) {
//   return (
//     <BakeasySelects
//       label={t('ORDERS')}
//       placeholder={t('SELECT') + t('ORDERS')}
//       size="small"
//       options={ORDERS_ARRAY}
//       fullWidth={true}
//       value={props?.value}
//       required={props?.required ?? false}
//       onChange={props?.onChange}
//     />
//   );
// }

// export function BakeasySelectLanguage(props: BakeasySelectProps) {
//   return (
//     <BakeasySelects
//       label={t('LANGUAGE')}
//       placeholder={t('SELECT') + t('LANGUAGE')}
//       size="small"
//       options={LANGUAGES_ARRAY}
//       fullWidth={true}
//       value={props?.value}
//       required={props?.required ?? false}
//       onChange={props?.onChange}
//       disabled={props?.disabled}
//     />
//   );
// }

// export function BillTo(props: BakeasySelectProps) {
//   return (
//     <BakeasySelects
//       label={t('BILL_TO')}
//       placeholder={t('SELECT') + t('BILL_TO')}
//       size="small"
//       options={BILL_TO_ARRAY}
//       fullWidth={true}
//       value={props?.value}
//       required={true}
//       onChange={props?.onChange}
//       disabled={props?.disabled}
//     />
//   );
// }

export function PaymentMethod(props: BakeasySelectProps) {
  return (
    <BakeasySelects
      label={t('METHOD')}
      placeholder={t('SELECT_PAYMAENT_METHOD')}
      size="small"
      options={PAYMENT_METHODS_ARRAY}
      fullWidth={true}
      value={props?.value}
      required={true}
      onChange={props?.onChange}
      disabled={props?.disabled}
    />
  );
}

export function PaymentTerms(props: BakeasySelectProps) {
  return (
    <BakeasySelects
      label={t('TERMS')}
      placeholder={t('SELECT_PAYMENT_TERMS')}
      size="small"
      options={PAYMENT_TERMS_ARRAY}
      fullWidth={true}
      value={props?.value}
      required={true}
      onChange={props?.onChange}
      disabled={props?.disabled}
    />
  );
}

export function SetupFee(props: BakeasySelectProps) {
  return (
    <BakeasySelects
      label={t('SETUP_FEE')}
      placeholder={t('SELECT_SETUP_FEE')}
      size="small"
      options={SETUP_FEE_ARRAY}
      fullWidth={true}
      value={props?.value}
      required={true}
      onChange={props?.onChange}
      disabled={props?.disabled}
    />
  );
}

export function SubscriptionPack(props: BakeasySelectProps) {
  return (
    <BakeasySelects
      label={t('SUBSCRIPTION_PACK')}
      placeholder={t('SELECT_SUBSCRIPTION_PACK')}
      size="small"
      options={SUBSCRIPTION_PACK_ARRAY}
      fullWidth={true}
      value={props?.value}
      required={true}
      onChange={props?.onChange}
      disabled={props?.disabled}
    />
  );
}

// export function TaxRateOne(props: BakeasySelectProps) {
//   return (
//     <BakeasySelects
//       label={t('RATE_1')}
//       placeholder={t('SELECT') + t('RATE_1')}
//       size="small"
//       options={TAXES_RATE_1_ARRAY}
//       fullWidth={true}
//       value={props?.value}
//       required={true}
//       onChange={props?.onChange}
//       disabled={props?.disabled}
//     />
//   );
// }

// export function TaxRateTwo(props: BakeasySelectProps) {
//   return (
//     <BakeasySelects
//       label={t('RATE_2')}
//       placeholder={t('SELECT') + t('RATE_2')}
//       size="small"
//       options={TAXES_RATE_2_ARRAY}
//       fullWidth={true}
//       value={props?.value}
//       required={true}
//       onChange={props?.onChange}
//       disabled={props?.disabled}
//     />
//   );
// }

export function ShipTo(props: BakeasySelectProps) {
  return (
    <BakeasySelects
      label={t('SHIP_TO')}
      placeholder={t('SELECT_SHIP_TO')}
      size="small"
      options={SHIP_TO_ARRAY}
      fullWidth={true}
      value={props?.value}
      required={true}
      onChange={props?.onChange}
      disabled={props?.disabled}
    />
  );
}

export function YesNo(props: BakeasySelectProps) {
  let options: any = i18n.language === 'fr' ? YES_NO_ARRAY_FR : YES_NO_ARRAY;
  return (
    <BakeasySelects
      label={props?.label}
      placeholder={props?.placeholder}
      size="small"
      options={options}
      fullWidth={true}
      value={props?.value}
      required={true}
      onChange={props?.onChange}
      disabled={props?.disabled}
    />
  );
}

export function ShippingCalculation(props: BakeasySelectProps) {
  return (
    <BakeasySelects
      label={t('SHIPPING_CALCULATION')}
      placeholder={t('SELECT_SHIPPING_CALCULATION')}
      size="small"
      options={SHIPPIING_CALCULATION_ARRAY}
      disabled={props?.disabled}
      fullWidth={true}
      value={props?.value}
      required={props?.required ?? false}
      onChange={props?.onChange}
    />
  );
}

// export function PreferredShippingMethod(props: BakeasySelectProps) {
//   return (
//     <BakeasySelects
//       label={t('PREFERRED_SHIPPING_METHOD')}
//       placeholder={t('SELECT_PREFERRED_SHIPPING_METHOD')}
//       size="small"
//       options={PREFERRED_SHIPPING_METHOD_ARRAY}
//       disabled={props?.disabled}
//       fullWidth={true}
//       value={props?.value}
//       required={true}
//       onChange={props?.onChange}
//     />
//   );
// }

// export function SelectProductType(props: BakeasySelectProps) {
//   return (
//     <BakeasySelects
//       label={t('PRODUCT_TYPE')}
//       placeholder={t('SELECT') + t('PRODUCT_TYPE')}
//       size="small"
//       options={PRODUCT_TYPE_ARRAY}
//       fullWidth={true}
//       value={props?.value}
//       onChange={props?.onChange}
//       disabled={props?.disabled}
//     />
//   );
// }

// export function SelectRecipeBuilder(props: BakeasySelectProps) {
//   return (
//     <BakeasySelects
//       disabled={props.disabled}
//       label={t('RECIPE_BUILDER')}
//       placeholder={t('SELECT') + t('RECIPE_BUILDER')}
//       size="small"
//       options={RECIPE_BUILDER_ARRAY}
//       fullWidth={true}
//       value={props?.value}
//       onChange={props?.onChange}
//     />
//   );
// }

// export function SelectProductName(props: BakeasySelectProps) {
//   return (
//     <BakeasySelects
//       label={t('PRODUCT_NAME')}
//       placeholder={t('SELECT') + t('PRODUCT_NAME')}
//       size="small"
//       options={PRODUCT_TYPE_ARRAY}
//       fullWidth={true}
//       value={props?.value}
//       onChange={props?.onChange}
//       disabled={props?.disabled}
//     />
//   );
// }

export function Priority(props: BakeasySelectProps) {
  return (
    <BakeasySelects
      label={t('PRIORITY')}
      placeholder={t('SELECT_PRIORITY')}
      size="small"
      options={PRIORITY_ARRAY}
      fullWidth={true}
      value={props?.value}
      required={props?.required ?? false}
      onChange={props?.onChange}
    />
  );
}

export function MessageStatus(props: BakeasySelectProps) {
  return (
    <BakeasySelects
      label={t('STATUS')}
      placeholder={t('SELECT_STATUS')}
      size="small"
      options={MESSAGE_STATUS_ARRAY}
      fullWidth={true}
      value={props?.value}
      required={props?.required ?? false}
      onChange={props?.onChange}
      disabled={props?.disabled}
    />
  );
}
